import {
  FormProvider,
  getFormProps,
  getTextareaProps,
  useField,
  useForm,
  useInputControl,
} from '@conform-to/react'
import {parseWithZod} from '@conform-to/zod'
import {
  json,
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import {
  Form,
  Link,
  NavLink,
  type ShouldRevalidateFunctionArgs,
  useActionData,
  useLoaderData,
} from '@remix-run/react'
import {
  Anchor,
  Button,
  CheckIcon,
  Copy01Icon,
  Menu01Icon,
  SwitchHorizontal01Icon,
  XCloseIcon,
} from '@translated/babele-react'
import clsx from 'clsx'
import React, {useCallback} from 'react'
import {mergeRefs} from 'react-merge-refs'
import {useDebounceSubmit} from 'remix-utils/use-debounce-submit'
import invariant from 'tiny-invariant'
import {z} from 'zod'

import {
  NavigationMenuContent,
  NavigationMenuSheet,
  NavigationMenuSheetClose,
  NavigationMenuSheetTrigger,
} from '~/features/NavigationMenuSheet'
import {getMemories} from '~/models/memory/memory.server'
import {postTranslate} from '~/models/translate/translate.server'
import {LanguagePickerConform} from '~/routes/translate/LanguagePicker'
import {Dialog, DialogContent, DialogTrigger} from '~/ui/Modal'
import {ModernMTLogo} from '~/ui/ModernMTLogo'
import {getUserToken} from '~/utils/auth'
import {useClipboard} from '~/utils/clipboard'
import {getBannerCookie} from '~/utils/cookieBanner.server'
import {
  getTranslateDefaults,
  setTranslateDefaults,
} from '~/utils/defaults.server'
import {translateSupportedLanguages} from '~/utils/locale'
import {destroySession, getSession} from '~/utils/sessions.server'
import {decodeJWT} from '~/utils/token.server'
import {CookieBanner} from './CookieBanner'
import {TaxiPageUnderConstruction} from './Images'
import {MemoryHandler} from './MemoryHandler'
import {getDetectedLanguageName} from './utils'

const TEXT_MAX_LENGTH = 5000
const DEBOUNCE_TIMEOUT = 800

export const meta: MetaFunction<typeof loader> = () => {
  return [
    {title: `ModernMT | Translate`},
    {
      name: 'description',
      content: `ModernMT | Translate`,
    },
  ]
}

export function shouldRevalidate({formAction}: ShouldRevalidateFunctionArgs) {
  return formAction != '/translate'
}

const TextareaAutoGrowing = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {onClear?: () => void}
>(function TextAreaAutoGrowing({className, onClear, ...props}, ref) {
  const parentRef = React.useRef<HTMLDivElement>(null)
  const textAreaRef = React.useRef<HTMLTextAreaElement>(null)

  invariant(props.name, 'The name prop is required for the field')

  const [meta] = useField<string>(props.name)
  const control = useInputControl(meta)

  return (
    <div
      ref={parentRef}
      className={clsx(
        'grid grid-cols-1 text-md md:text-display-xs after:px-8 after:py-6 relative',
        '[&>textarea]:tracking-[inherit] [&>textarea]:text-inherit after:text-inherit [&>textarea]:resize-none [&>textarea]:overflow-hidden [&>textarea]:[grid-area:1/1/2/2]',
        "after:[grid-area:1/1/2/2] after:whitespace-pre-wrap after:invisible after:content-[attr(data-cloned-val)_'_']",
        className,
      )}
    >
      <textarea
        ref={mergeRefs([textAreaRef, ref])}
        className={clsx(
          'w-full appearance-none px-8 py-6 outline-none',
          'placeholder:text-neutral-700',
          'whitespace-pre-wrap',
        )}
        {...props}
        onChange={(e) => {
          if (!parentRef.current) return
          const value = e.currentTarget.value
          parentRef.current.dataset.clonedVal = value
          control.change(value.trim())
          props.onChange?.(e)
        }}
        {...getTextareaProps(meta)}
      />
      {control.value && (
        <Button
          variant={{mode: 'ghost', size: 'xs'}}
          aria-label="Clear text"
          className="absolute right-0 top-6"
          onClick={() => {
            onClear?.()
            parentRef.current!.dataset.clonedVal = ''
            control.change('')
            textAreaRef.current?.focus()
          }}
        >
          <XCloseIcon />
        </Button>
      )}
    </div>
  )
})

function TranslationResult({
  translatedText,
  translationState,
}: {
  translatedText: string | undefined
  translationState: TranslationState
}) {
  const classNames =
    'w-full min-w-0 outline-none py-6 px-8 text-md md:text-display-xs whitespace-pre-wrap break-words'
  const dataTestId = 'translated-text'

  if (translationState == 'ready') {
    return (
      <div
        data-testid={dataTestId}
        className={clsx(classNames, 'text-neutral-700')}
      >
        Translate
      </div>
    )
  }

  if (translationState == 'starting') {
    return (
      <div
        data-testid={dataTestId}
        className={clsx(classNames, 'animate-pulse')}
      >
        Translating...
      </div>
    )
  }

  if (translationState == 'translated') {
    return (
      <div data-testid={dataTestId} className={classNames}>
        {translatedText}
      </div>
    )
  }

  if (translationState == 'updating') {
    return (
      <div data-testid={dataTestId} className={classNames}>
        {translatedText}...
      </div>
    )
  }

  return null
}

const menuItems = [
  {label: 'For enterprises', href: '/', reloadDocument: true},
  {label: 'For translators', href: '/translators', reloadDocument: true},
  {label: 'Pricing plans', href: '/pricing', reloadDocument: true},
  {label: 'Translate', href: '/translate', reloadDocument: false},
] as const

export const translateSchema = z.object({
  text: z.string(),
  sourceLanguage: z.string().optional(),
  targetLanguage: z.string(),
  activeMemories: z
    .string()
    .optional()
    .transform((value) => {
      return value ? value.split(',').map((v) => parseInt(v, 10)) : []
    }),
})

type translateForm = z.infer<typeof translateSchema>

export const loader = async ({request}: LoaderFunctionArgs) => {
  const cookieBanner = await getBannerCookie({request})

  const {activeMemories, sourceLanguage, targetLanguage} =
    await getTranslateDefaults({request})

  const token = await getUserToken(request)
  if (!token)
    return json({
      licenseKey: null,
      memories: null,
      defaultSourceLanguage: sourceLanguage,
      defaultTargetLanguage: targetLanguage,
      cookieBanner,
    })

  const licenseKey = decodeJWT(token).token

  const memories = await getMemories({token})

  if ('error' in memories) {
    if (memories.error.statusCode == 401) {
      const session = await getSession(request.headers.get('Cookie'))
      return json(
        {
          licenseKey: null,
          memories: null,
          defaultSourceLanguage: sourceLanguage,
          defaultTargetLanguage: targetLanguage,
          cookieBanner,
        },
        {status: 401, headers: {'Set-Cookie': await destroySession(session)}},
      )
    }
    throw json({error: memories.error.message}, {status: 500})
  }

  return json({
    licenseKey,
    memories: memories.data.map((memory) => ({
      ...memory,
      isActive: activeMemories.includes(memory.id),
    })),
    defaultSourceLanguage: sourceLanguage,
    defaultTargetLanguage: targetLanguage,
    cookieBanner,
  })
}

export const action = async ({request}: ActionFunctionArgs) => {
  const token = await getUserToken(request)
  const formData = await request.formData()

  let defaults = await getTranslateDefaults({request})
  const submission = await parseWithZod(formData, {
    async: true,
    schema: translateSchema.transform(async (data, ctx) => {
      if (!data.text?.trim().length) return

      defaults['sourceLanguage'] = data.sourceLanguage ?? ''
      defaults['targetLanguage'] = data.targetLanguage

      const res = await postTranslate({
        ...data,
        token,
        sourceLanguage: data.sourceLanguage ?? '',
        activeMemories: defaults.activeMemories,
      })

      if ('error' in res) {
        ctx.addIssue({code: z.ZodIssueCode.custom, message: res.error.message})
        return z.NEVER
      }
      return res.data
    }),
  })

  const reply = submission.reply()

  return json(
    {
      ...reply,
      value: submission.status == 'success' ? submission.value : undefined,
    },
    {
      headers: {
        'Set-Cookie': await setTranslateDefaults(defaults),
      },
    },
  )
}

export default function TranslateLayout() {
  const loaderData = useLoaderData<typeof loader>()

  return (
    <div className="min-h-below-strip bg-gradient-to-b from-[#fef7e6] via-[400px] via-[#fef7e6] to-neutral-25 to-[400px]">
      <header
        className={clsx(
          'w-[86%] mx-auto max-w-[1400px] h-[80px] md:h-[104px]',
          'flex items-center',
        )}
      >
        <NavLink to="/" reloadDocument>
          <div className="max-md:max-w-[150px] w-[200px]">
            <ModernMTLogo className="w-full" />
          </div>
        </NavLink>
        <NavigationMenuSheet>
          <NavigationMenuSheetTrigger asChild>
            <Button
              className="ml-auto lg:hidden flex"
              aria-label="Toggle navigation menu"
              variant={{mode: 'ghost', tone: 'neutral'}}
            >
              <Menu01Icon variant={{size: 'md'}} />
            </Button>
          </NavigationMenuSheetTrigger>
          <NavigationMenuContent>
            <div className="py-8 px-12 flex flex-col gap-10">
              <NavigationMenuSheetClose asChild>
                <Button
                  variant={{mode: 'ghost'}}
                  aria-label="Close navigation menu"
                  className="ml-auto"
                >
                  <XCloseIcon />
                </Button>
              </NavigationMenuSheetClose>
              <nav className="flex flex-col items-end gap-3 *:text-neutral-25">
                {menuItems.map((item, index) => (
                  <Anchor
                    variant={{tone: 'neutral', size: 'lg'}}
                    key={index}
                    className={clsx(
                      'shrink-0 aria-[current=page]:text-neutral-600',
                    )}
                    render={
                      <NavLink
                        to={item.href}
                        reloadDocument={item.reloadDocument}
                      >
                        {item.label}
                      </NavLink>
                    }
                  />
                ))}
                {loaderData.licenseKey ? (
                  <Button
                    variant={{mode: 'outline', tone: 'neutral', size: 'lg'}}
                    render={<NavLink to="/dashboard">My Dashboard</NavLink>}
                    className="my-2 hover:!bg-neutral-25/1 border-neutral-800"
                  >
                    My Dashboard
                  </Button>
                ) : (
                  <Anchor
                    variant={{tone: 'neutral', size: 'lg'}}
                    render={<NavLink to="/login">Login</NavLink>}
                  />
                )}
              </nav>
            </div>
          </NavigationMenuContent>
        </NavigationMenuSheet>
        <nav className="ml-auto items-center gap-5 lg:flex hidden px-5">
          {menuItems.map((item, index) => (
            <Anchor
              variant={{tone: 'neutral'}}
              key={index}
              className="shrink-0 aria-[current=page]:text-neutral-800"
              render={
                <NavLink to={item.href} reloadDocument={item.reloadDocument}>
                  {item.label}
                </NavLink>
              }
            />
          ))}
          {loaderData.licenseKey ? (
            <Button
              variant={{mode: 'outline', tone: 'primary'}}
              render={<NavLink to="/dashboard">My Dashboard</NavLink>}
            >
              My Dashboard
            </Button>
          ) : (
            <Anchor
              variant={{tone: 'neutral'}}
              render={<NavLink to="/login">Login</NavLink>}
            />
          )}
        </nav>
      </header>
      <main className="max-w-[1010px] mx-auto py-2 md:py-[6rem] px-6 md:px-10">
        <Translate />
      </main>
      <CookieBanner />
    </div>
  )
}

/**
 * ready: The initial state of the translation. The user has not entered any text yet
 * starting: The translation is starting. The user has entered text and the translation is being requested
 * translated: The translation has been completed and the translated text is displayed
 * updating: The translation is being updated. The user has changed the text, active memories, the source or target language
 */
type TranslationState = 'ready' | 'starting' | 'translated' | 'updating'

function Translate() {
  const loaderData = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const [translationState, setTranslationState] =
    React.useState<TranslationState>('ready')
  const isLoading =
    translationState == 'starting' || translationState == 'updating'

  const formRef = React.useRef<HTMLFormElement>(null)

  const submit = useDebounceSubmit()

  React.useEffect(() => {
    if (actionData)
      setTranslationState((state) => {
        if (state == 'ready') return state
        return 'translated'
      })
  }, [actionData])

  const {copyToClipboard, isCopied, isClipboardEnabled} = useClipboard()

  const [form, fields] = useForm<translateForm>({
    defaultValue: {
      text: '',
      sourceLanguage: loaderData.defaultSourceLanguage,
      targetLanguage: loaderData.defaultTargetLanguage,
    },
    onValidate: ({formData}) => {
      const submission = parseWithZod(formData, {schema: translateSchema})
      const reply = submission.reply()
      if (!reply.error) submitForm()
      return submission
    },
    shouldValidate: 'onInput',
    lastResult: actionData,
    id: 'translate-form',
  })

  const translatedText =
    actionData?.status == 'success' && fields.text.value?.length
      ? actionData.value?.translation
      : undefined

  const submitForm = useCallback(() => {
    setTranslationState((state) =>
      ['ready', 'starting'].includes(state) ? 'starting' : 'updating',
    )

    submit(formRef.current, {
      debounceTimeout: DEBOUNCE_TIMEOUT,
    })
  }, [submit])

  const detectedLanguage = actionData?.value?.detectedLanguage
  const detectedLanguageName = getDetectedLanguageName(detectedLanguage)

  return (
    <FormProvider context={form.context}>
      <Form {...getFormProps(form)} method="POST" ref={formRef} />
      <div className="flex flex-col gap-8">
        <div className="grid grid-cols-2 gap-y-4">
          <div className="md:text-lg flex items-center gap-2 row-start-1 col-start-1 justify-center md:justify-start px-3 md:px-0">
            <label
              className="shrink-0 hidden md:inline"
              id="translate-from-label"
            >
              Translate from
            </label>
            <LanguagePickerConform
              ariaLabelledBy="translate-from-label"
              meta={fields.sourceLanguage}
              autoDetectItem
              detectedLanguage={detectedLanguage}
            />
          </div>
          <div className="md:text-lg flex items-center gap-2 row-start-1 col-start-2 justify-center md:justify-start px-3 md:px-0">
            <label
              className="shrink-0 hidden md:inline"
              id="translate-to-label"
            >
              Translate to
            </label>
            <LanguagePickerConform
              meta={fields.targetLanguage}
              ariaLabelledBy="translate-to-label"
            />
          </div>
          <div
            className={clsx(
              'md:row-start-2 col-span-full',
              'grid md:grid-cols-2 grid-rows-[auto_1fr_auto_auto_1fr_auto] md:grid-rows-[1fr_auto]',
              'rounded-lg bg-neutral-25',
              'min-h-[360px]',
            )}
          >
            <div
              className={clsx(
                'row-span-3 md:row-span-full grid grid-rows-subgrid border-1 border-neutral-400 relative',
                'max-md:rounded-t-lg md:rounded-s-lg overflow-hidden',
                'focus-within:border-primary-500 focus-within:hover:border-1 focus-within:border-1 hover:focus-within:border-primary-500',
                'focus-within:ring-4 ring-primary-500/3',
              )}
            >
              {(fields.sourceLanguage.value || detectedLanguageName) && (
                <span className="text-md md:hidden font-bold px-8 pt-6">
                  {detectedLanguageName
                    ? detectedLanguageName
                    : translateSupportedLanguages.find(
                        (locale) => locale.code == fields.sourceLanguage.value,
                      )?.name}
                </span>
              )}

              <div className="grid pr-6 relative">
                <TextareaAutoGrowing
                  autoFocus
                  maxLength={TEXT_MAX_LENGTH}
                  name={fields.text.name}
                  placeholder="Insert text"
                  aria-label="Text to translate"
                  onClear={() => {
                    setTranslationState('ready')
                  }}
                  onChange={(e) => {
                    if (e.currentTarget.value == '')
                      setTranslationState('ready')
                  }}
                />
              </div>

              <div className="px-8 pb-6 ml-auto mt-auto">
                {fields.text.value?.length ?? 0} / {TEXT_MAX_LENGTH}
              </div>
            </div>

            <div
              className={clsx(
                'row-span-3 md:row-span-full grid grid-rows-subgrid border-neutral-400',
                'max-md:border-l-1 border-r-1 md:border-t-1  border-b-1',
                'max-md:rounded-b-lg md:rounded-e-lg overflow-hidden',
              )}
            >
              {fields.targetLanguage.value && (
                <div className="text-md md:hidden font-bold px-8 pt-6">
                  {
                    translateSupportedLanguages.find(
                      (locale) => locale.code == fields.targetLanguage.value,
                    )?.name
                  }
                </div>
              )}

              <div className="pr-6 grid">
                <TranslationResult
                  translatedText={translatedText}
                  translationState={translationState}
                />
              </div>
              <div className="px-8 pb-6 ml-auto mt-auto">
                {!!isClipboardEnabled && translatedText && (
                  <Button
                    onClick={async () => {
                      await copyToClipboard(translatedText)
                    }}
                    variant={{tone: 'neutral', size: 'md', mode: 'ghost'}}
                    aria-label="Copy to clipboard"
                  >
                    {!isCopied ? <Copy01Icon /> : <CheckIcon />}
                  </Button>
                )}
              </div>
            </div>
          </div>
          <div className="row-start-1 md:row-start-2 col-span-full m-auto bg-transparent relative">
            <div className="p-1 md:bg-neutral-25 rounded-full">
              <Button
                type="button"
                form="translate-form"
                style={{rotate: '0deg'}}
                variant={{mode: 'ghost', shape: 'round'}}
                className="!transition-all"
                aria-label="Switch"
                disabled={isLoading}
                onClick={(e) => {
                  e.preventDefault()

                  const sourceLanguageValue =
                    actionData?.value?.detectedLanguage ||
                    fields.sourceLanguage.value
                  const targetLanguageValue = fields.targetLanguage.value

                  const currentRotation = parseInt(e.currentTarget.style.rotate)
                  e.currentTarget.style.rotate = `${currentRotation + 180}deg`

                  form.update({
                    name: form.name,
                    value: {
                      text: translatedText,
                      sourceLanguage: targetLanguageValue,
                      targetLanguage: sourceLanguageValue,
                    },
                  })
                }}
              >
                <SwitchHorizontal01Icon />
              </Button>
            </div>
          </div>
        </div>
        {form.errors && (
          <div className="p-4 bg-critical-100 text-critical-1000">
            {form.errors[0]}
          </div>
        )}
        {!loaderData.licenseKey ? (
          <div
            className={clsx(
              'border-1 border-neutral-400 rounded-lg p-6 flex justify-between gap-3 flex-col',
              'sm:flex-row items-start sm:items-center ',
            )}
          >
            <div className="flex flex-col gap-0.5">
              <div className="text-display-sm">Improve your translations</div>
              <div className="text-sm text-neutral-800">
                Import your translation memories to see the power of ModernMT
              </div>
            </div>
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant={{tone: 'primary', size: 'lg'}}
                  className="w-full sm:w-fit"
                >
                  Import memories
                </Button>
              </DialogTrigger>
              <DialogContent className="!max-w-lg">
                <div className="flex flex-col items-center gap-6">
                  <div className="py-6">
                    <TaxiPageUnderConstruction className="mx-auto size-[224px] object-fill" />
                  </div>

                  <div className="text-display-md text-center">
                    Want to experience the power of ModernMT?
                  </div>
                  <div>Signup to one of our plans</div>

                  <Button
                    variant={{tone: 'primary', mode: 'solid', size: 'lg'}}
                    render={
                      <Link reloadDocument to="/pricing">
                        View our plans
                      </Link>
                    }
                  >
                    View our plans
                  </Button>
                  <div>
                    Already have an account?{' '}
                    <Anchor render={<Link to="/login">Login</Link>}>
                      Login
                    </Anchor>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        ) : (
          <MemoryHandler
            memories={loaderData.memories}
            onActiveMemoriesChange={() => {
              if (fields.text.value?.trim().length) {
                setTranslationState('updating')
                submitForm()
              }
            }}
          />
        )}
      </div>
    </FormProvider>
  )
}
